import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PoolIcon from '@mui/icons-material/Pool';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CelebrationIcon from '@mui/icons-material/Celebration';
import GroupIcon from '@mui/icons-material/Group';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import axiosInstance from '../../api/axiosConfig';
import swimService from '../../api/services/swimService';
import proBookingService from '../../api/services/proBookingService';
import { AttendanceStatus, CoachCalendarDay, CoachCalendarSession, SwimCompetitionRow, SwimEventRow, SwimLeague } from '../../types/swim';
import { Pro } from '../../types';
import { useIsMobile } from '../../hooks/useIsMobile';
import SessionDetailDialog from './SessionDetailDialog';

interface SwimProgramOption {
  id: string;
  name: string;
}

interface CoachOption {
  staff_id: string;
  staff_name: string;
}

interface Props {
  day: CoachCalendarDay;
  onClose: () => void;
  onSaved: () => void;
}

interface CompetitionForm {
  id?: string;
  name: string;
  location: string;
  host_team: string;
  start_time: string;
  end_time: string;
  notes: string;
  program_ids: string[];
  coach_ids: string[];
  league_id: string;
}

interface EventForm {
  id?: string;
  name: string;
  location: string;
  start_time: string;
  end_time: string;
  notes: string;
}

const emptyEventForm: EventForm = {
  name: '',
  location: '',
  start_time: '',
  end_time: '',
  notes: '',
};

function fromCompetition(c: SwimCompetitionRow): CompetitionForm {
  return {
    id: c.id,
    name: c.name,
    location: c.location ?? '',
    host_team: c.host_team ?? '',
    start_time: c.start_time ?? '',
    end_time: c.end_time ?? '',
    notes: c.notes ?? '',
    program_ids: c.program_ids ?? [],
    coach_ids: c.coach_ids ?? [],
    league_id: c.league_id ?? '',
  };
}

const emptyCompetition: CompetitionForm = {
  name: '',
  location: '',
  host_team: '',
  start_time: '',
  end_time: '',
  notes: '',
  program_ids: [],
  coach_ids: [],
  league_id: '',
};

function to12Hour(hhmm: string | null): string {
  if (!hhmm) return '';
  const [hStr, m] = hhmm.split(':');
  const h = Number(hStr);
  if (!Number.isFinite(h)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m ?? '00'} ${period}`;
}

function prettyDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

const DayDetailDialog: React.FC<Props> = ({ day, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [dailyNote, setDailyNote] = useState<string>(day.workout ?? '');
  const [showCompForm, setShowCompForm] = useState<boolean>(day.competitions.length > 0);
  const [compForm, setCompForm] = useState<CompetitionForm>(
    day.competitions[0] ? fromCompetition(day.competitions[0]) : emptyCompetition,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [programs, setPrograms] = useState<SwimProgramOption[]>([]);
  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [leagues, setLeagues] = useState<SwimLeague[]>([]);
  // Drill-into-session: when set, render the per-session dialog on top.
  const [openSession, setOpenSession] = useState<CoachCalendarSession | null>(null);
  // Inline event editor — null when closed, a form object when adding/editing.
  const [eventForm, setEventForm] = useState<EventForm | null>(null);
  const [eventBusy, setEventBusy] = useState(false);
  // Per-session counts: enrolled (roster size) and attended (present/late/makeup).
  const [sessionCounts, setSessionCounts] = useState<
    Record<string, { signed_up: number; attended: number; has_records: boolean }>
  >({});

  function openNewEvent() {
    setEventForm({ ...emptyEventForm });
  }
  function openEditEvent(ev: SwimEventRow) {
    setEventForm({
      id: ev.id,
      name: ev.name,
      location: ev.location ?? '',
      start_time: ev.start_time ?? '',
      end_time: ev.end_time ?? '',
      notes: ev.notes ?? '',
    });
  }
  async function saveEvent() {
    if (!eventForm) return;
    if (!eventForm.name.trim()) {
      setError('Event name is required.');
      return;
    }
    setEventBusy(true);
    setError(null);
    try {
      await swimService.upsertEvent({
        id: eventForm.id,
        event_date: day.date,
        name: eventForm.name.trim(),
        location: eventForm.location.trim() || undefined,
        start_time: eventForm.start_time || undefined,
        end_time: eventForm.end_time || undefined,
        notes: eventForm.notes.trim() || undefined,
      });
      setEventForm(null);
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setEventBusy(false);
    }
  }
  async function removeEvent(ev: SwimEventRow) {
    if (!window.confirm(`Delete "${ev.name}"? This can't be undone.`)) return;
    setEventBusy(true);
    setError(null);
    try {
      await swimService.deleteEvent(ev.id);
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Delete failed');
    } finally {
      setEventBusy(false);
    }
  }

  // Sync from prop on day change
  useEffect(() => {
    setDailyNote(day.workout ?? '');
    setShowCompForm(day.competitions.length > 0);
    setCompForm(day.competitions[0] ? fromCompetition(day.competitions[0]) : emptyCompetition);
    setError(null);
  }, [day]);

  // Fetch enrollment + attendance counts for each session on this day.
  // Refreshes whenever the day's session list changes (e.g. after onSaved
  // bubbles up a re-fetch with fresh data).
  const sessionIdsKey = (day.sessions ?? []).map((s) => s.id).join('|');
  useEffect(() => {
    const sessions = day.sessions ?? [];
    if (sessions.length === 0) {
      setSessionCounts({});
      return;
    }
    let cancelled = false;

    void (async () => {
      const results = await Promise.all(
        sessions.map(async (s) => {
          const [rosterRes, attRes] = await Promise.allSettled([
            swimService.getRoster(s.program_id, { date: day.date }),
            swimService.getSessionAttendance(s.id),
          ]);
          const signed_up = rosterRes.status === 'fulfilled' ? rosterRes.value.count : 0;
          const records =
            attRes.status === 'fulfilled'
              ? ((attRes.value?.data ?? []) as Array<{ status: AttendanceStatus }>)
              : [];
          const attended = records.filter(
            (r) => r.status === 'present' || r.status === 'late' || r.status === 'makeup',
          ).length;
          return { id: s.id, signed_up, attended, has_records: records.length > 0 };
        }),
      );
      if (cancelled) return;
      const next: Record<string, { signed_up: number; attended: number; has_records: boolean }> = {};
      for (const r of results) {
        next[r.id] = { signed_up: r.signed_up, attended: r.attended, has_records: r.has_records };
      }
      setSessionCounts(next);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day.date, sessionIdsKey]);

  // Load published swim programs once for the competition gating multi-select.
  useEffect(() => {
    void (async () => {
      try {
        const res = await axiosInstance.get<{ data: Array<{ id: string; name: string; program_type: string; is_active: boolean }> }>(
          '/programs/all',
          { params: { include_inactive: 'true' } },
        );
        const rows = (res.data.data ?? [])
          .filter((p) => p.program_type === 'swim' && p.is_active)
          .map((p) => ({ id: p.id, name: p.name }));
        setPrograms(rows);
      } catch {
        // Non-blocking
      }
    })();
  }, []);

  // Load swim coaches for the competition picker + the session dialog (we
  // pass coaches down rather than reloading per session).
  useEffect(() => {
    void (async () => {
      try {
        const pros = await proBookingService.getAllPros();
        const swim = pros
          .filter((p: Pro) =>
            (p.departments ?? []).some((d) => String(d).toLowerCase() === 'swim_program'),
          )
          .map((p: Pro) => ({
            staff_id: p.id,
            staff_name: `${p.first_name} ${p.last_name}`.trim(),
          }));
        setCoaches(swim);
      } catch {
        // Non-blocking
      }
    })();
  }, []);

  // Load leagues for the competition's league selector.
  useEffect(() => {
    void (async () => {
      try {
        setLeagues(await swimService.getLeagues());
      } catch {
        // Non-blocking
      }
    })();
  }, []);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      // Save daily note if changed (this is the broadcast note, not per-class)
      const noteChanged = (dailyNote || '') !== (day.workout || '');
      if (noteChanged) {
        await swimService.setDailyWorkout(day.date, dailyNote.trim() || null);
      }
      // Save competition if visible & valid
      if (showCompForm) {
        if (!compForm.name.trim()) {
          setError('Competition name is required when a competition is added.');
          setBusy(false);
          return;
        }
        await swimService.upsertCompetition({
          id: compForm.id,
          competition_date: day.date,
          name: compForm.name.trim(),
          league_id: compForm.league_id || null,
          location: compForm.location.trim() || undefined,
          host_team: compForm.host_team.trim() || undefined,
          start_time: compForm.start_time || undefined,
          end_time: compForm.end_time || undefined,
          notes: compForm.notes.trim() || undefined,
          program_ids: compForm.program_ids,
          coach_ids: compForm.coach_ids,
        });
      } else if (day.competitions[0]) {
        // Toggle off → delete existing competition
        await swimService.deleteCompetition(day.competitions[0].id);
      }
      onSaved();
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  const sessionsForDay = day.sessions ?? [];

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PoolIcon color="primary" />
            {prettyDate(day.date)}
          </Stack>
          {day.session_count > 0 && (
            <Typography variant="caption" color="text.secondary">
              {day.session_count} swim session{day.session_count === 1 ? '' : 's'} scheduled
              {day.cancelled_session_count > 0 && ` (${day.cancelled_session_count} cancelled)`}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Sessions list — primary content. Each session opens its own dialog. */}
          {sessionsForDay.length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <PoolIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2">Classes today</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Tap a class to set its workout, assign coaches, and see today's roster with emergency contacts.
              </Typography>
              <Stack
                divider={<Divider />}
                sx={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 3,
                }}
              >
                {sessionsForDay.map((s) => {
                  const timeLabel = s.start_time
                    ? `${to12Hour(s.start_time)}${s.end_time ? ` – ${to12Hour(s.end_time)}` : ''}`
                    : null;
                  const staffNames = (s.staff ?? [])
                    .map((a) => a.staff_name)
                    .filter((n): n is string => !!n);
                  const counts = sessionCounts[s.id];
                  return (
                    <Box
                      key={s.id}
                      onClick={() => setOpenSession(s)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          setOpenSession(s);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.5,
                        py: 1.25,
                        cursor: 'pointer',
                        userSelect: 'none',
                        opacity: s.is_cancelled ? 0.55 : 1,
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' },
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>
                            {s.program_name ?? 'Program'}
                          </Typography>
                          <Chip
                            label={s.target_audience}
                            size="small"
                            color={
                              s.target_audience === 'adult' ? 'secondary'
                              : s.target_audience === 'all' ? 'default'
                              : 'primary'
                            }
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {timeLabel ?? 'No time set'}
                          {s.is_cancelled && ' • cancelled'}
                          {s.workout && ' • workout written'}
                        </Typography>
                        {counts && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              icon={<GroupIcon sx={{ fontSize: 12 }} />}
                              label={`${counts.signed_up} signed up`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
                            />
                            {counts.has_records && (
                              <Chip
                                icon={<HowToRegIcon sx={{ fontSize: 12 }} />}
                                label={`${counts.attended} attended`}
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { ml: 0.5 } }}
                              />
                            )}
                          </Stack>
                        )}
                        {staffNames.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            {staffNames.map((n) => (
                              <Chip key={n} label={n} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                            ))}
                          </Stack>
                        )}
                        {staffNames.length === 0 && !s.is_cancelled && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                            No coach assigned
                          </Typography>
                        )}
                      </Box>
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

          {/* Lessons — read-only listing of swim pro bookings on this date. */}
          {(day.lessons ?? []).length > 0 && (
            <>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">Private lessons</Typography>
                <Chip
                  label={`${(day.lessons ?? []).length}`}
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Pro-led 1:1 lessons booked through Pro Booking. Read-only here — manage in Pro Bookings.
              </Typography>
              <Stack
                divider={<Divider />}
                sx={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  mb: 3,
                }}
              >
                {(day.lessons ?? []).map((lesson) => {
                  const startDt = lesson.start_time ? new Date(lesson.start_time) : null;
                  const endDt = lesson.end_time ? new Date(lesson.end_time) : null;
                  const timeLabel = startDt
                    ? `${startDt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}${
                        endDt ? ` – ${endDt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}` : ''
                      }`
                    : null;
                  return (
                    <Box key={lesson.id} sx={{ p: 1.5 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {lesson.pro_name ?? 'Pro'}
                            {lesson.participant_label && (
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                                with {lesson.participant_label}
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {timeLabel ?? '—'}
                          </Typography>
                        </Box>
                        {lesson.status && lesson.status !== 'confirmed' && (
                          <Chip
                            label={lesson.status}
                            size="small"
                            color={lesson.status === 'completed' ? 'default' : 'warning'}
                            variant="outlined"
                            sx={{ flexShrink: 0 }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </>
          )}

          {/* Daily note — broadcast info for the whole pool, separate from per-class workouts. */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <EventNoteIcon fontSize="small" color="success" />
            <Typography variant="subtitle2">Daily note</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Broadcast info for the whole pool. Use when something applies across every class (e.g. "pool closes at 6 for power outage"). For per-class workouts, tap the class above.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={`e.g. "Lap pool only — therapy pool closed for maintenance"`}
            value={dailyNote}
            onChange={(e) => setDailyNote(e.target.value)}
          />

          <Divider sx={{ my: 3 }} />

          {/* Competition section — unchanged. */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmojiEventsIcon fontSize="small" color="warning" />
              <Typography variant="subtitle2">Competition</Typography>
            </Stack>
            {!showCompForm ? (
              <Button size="small" onClick={() => setShowCompForm(true)}>+ Add competition</Button>
            ) : (
              <IconButton
                size="small"
                onClick={() => setShowCompForm(false)}
                title="Remove competition from this day"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {showCompForm && (
            <Stack spacing={2}>
              <TextField
                label="Name"
                required
                fullWidth
                placeholder="e.g. Clovis Invitational"
                value={compForm.name}
                onChange={(e) => setCompForm({ ...compForm, name: e.target.value })}
              />
              <FormControl fullWidth>
                <InputLabel id="meet-league-label">League / season</InputLabel>
                <Select
                  labelId="meet-league-label"
                  label="League / season"
                  value={compForm.league_id}
                  onChange={(e) => setCompForm({ ...compForm, league_id: e.target.value })}
                >
                  <MenuItem value=""><em>None (standalone meet)</em></MenuItem>
                  {leagues.map((lg) => (
                    <MenuItem key={lg.id} value={lg.id}>{lg.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Location"
                  fullWidth
                  placeholder="e.g. Clovis West HS"
                  value={compForm.location}
                  onChange={(e) => setCompForm({ ...compForm, location: e.target.value })}
                />
                <TextField
                  label="Host team"
                  fullWidth
                  placeholder="e.g. Clovis West Swimming"
                  value={compForm.host_team}
                  onChange={(e) => setCompForm({ ...compForm, host_team: e.target.value })}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="time"
                  label="Start time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={compForm.start_time}
                  onChange={(e) => setCompForm({ ...compForm, start_time: e.target.value })}
                />
                <TextField
                  type="time"
                  label="End time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={compForm.end_time}
                  onChange={(e) => setCompForm({ ...compForm, end_time: e.target.value })}
                />
              </Stack>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. Warm-ups 6:30 AM. Bring team suit."
                value={compForm.notes}
                onChange={(e) => setCompForm({ ...compForm, notes: e.target.value })}
              />
              <Box>
                <Autocomplete
                  multiple
                  options={programs}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  value={programs.filter((p) => compForm.program_ids.includes(p.id))}
                  onChange={(_, selected) =>
                    setCompForm({ ...compForm, program_ids: selected.map((s) => s.id) })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Programs this meet applies to"
                      placeholder={compForm.program_ids.length === 0 ? 'Leave empty for all swim parents' : ''}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Limit visibility to specific programs (e.g. Novice meets vs Masters meets). Leave empty for a
                  club-wide meet visible to every swim parent.
                </Typography>
              </Box>
              <Box>
                <Autocomplete
                  multiple
                  options={coaches}
                  getOptionLabel={(o) => o.staff_name}
                  isOptionEqualToValue={(a, b) => a.staff_id === b.staff_id}
                  value={coaches.filter((c) => compForm.coach_ids.includes(c.staff_id))}
                  onChange={(_, selected) =>
                    setCompForm({ ...compForm, coach_ids: selected.map((s) => s.staff_id) })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Coaches attending"
                      placeholder={compForm.coach_ids.length === 0 ? 'Who is taking the team?' : ''}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Which coaches are going to this meet. Shows up on the calendar so the team knows who's on deck.
                </Typography>
              </Box>
            </Stack>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Events (parties, banquets, fundraisers) — supports multiple per day. */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <CelebrationIcon fontSize="small" color="secondary" />
              <Typography variant="subtitle2">Events</Typography>
              {(day.events ?? []).length > 0 && (
                <Chip
                  label={`${(day.events ?? []).length}`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Stack>
            <Button size="small" onClick={openNewEvent} disabled={eventBusy || eventForm !== null}>
              + Add event
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Parties, banquets, fundraisers — broadcast to all swim parents on their schedule.
          </Typography>

          {(day.events ?? []).length > 0 && (
            <Stack
              divider={<Divider />}
              sx={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 1.5,
              }}
            >
              {(day.events ?? []).map((ev) => {
                const timeLabel = ev.start_time
                  ? `${to12Hour(ev.start_time)}${ev.end_time ? ` – ${to12Hour(ev.end_time)}` : ''}`
                  : null;
                return (
                  <Box key={ev.id} sx={{ p: 1.5 }}>
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ev.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[ev.location, timeLabel].filter(Boolean).join(' · ') || '—'}
                        </Typography>
                        {ev.notes && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {ev.notes}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0}>
                        <IconButton size="small" onClick={() => openEditEvent(ev)} disabled={eventBusy || eventForm !== null}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => void removeEvent(ev)} disabled={eventBusy}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          {eventForm !== null && (
            <Stack spacing={2} sx={{ p: 1.5, border: '1px dashed rgba(0,0,0,0.15)', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {eventForm.id ? 'Edit event' : 'New event'}
              </Typography>
              <TextField
                label="Name"
                required
                fullWidth
                placeholder="e.g. Swim Team Party"
                value={eventForm.name}
                onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
              />
              <TextField
                label="Location"
                fullWidth
                placeholder="e.g. Clubhouse Lawn"
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  type="time"
                  label="Start time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={eventForm.start_time}
                  onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                />
                <TextField
                  type="time"
                  label="End time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={eventForm.end_time}
                  onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                />
              </Stack>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                placeholder="e.g. Potluck — last names A–M bring sides"
                value={eventForm.notes}
                onChange={(e) => setEventForm({ ...eventForm, notes: e.target.value })}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={() => setEventForm(null)} disabled={eventBusy}>Cancel</Button>
                <Button variant="contained" onClick={() => void saveEvent()} disabled={eventBusy || !eventForm.name.trim()}>
                  {eventBusy ? 'Saving…' : eventForm.id ? 'Save event' : 'Add event'}
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void save()} variant="contained" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {openSession && (
        <SessionDetailDialog
          date={day.date}
          session={openSession}
          coaches={coaches}
          dailyNote={day.workout}
          onClose={() => setOpenSession(null)}
          onSaved={() => {
            setOpenSession(null);
            onSaved();
          }}
        />
      )}
    </>
  );
};

export default DayDetailDialog;
